import { computed, Injectable, signal } from '@angular/core';
import type { ReminderTrackerPresenceEntry } from '@features/reminder-tracker/types/reminder-tracker-presence.types';
import type { UUID } from '@kerberos-compliance/compliance-shared-ts/common/types/uuid.type';
import type { ResponseEmailDto } from '@kerberos-compliance/compliance-shared-ts/features/communication/dtos/response.email.dto';
import type { ResponseDeliveryTrackerCacheDto } from '@kerberos-compliance/compliance-shared-ts/features/delivery/dtos/response.delivery-tracker.cache-dto';
import { BaseStore } from '@kerberos-compliance/compliance-shared-ui';

@Injectable()
export class DeliveryTrackerDetailStore extends BaseStore<ResponseDeliveryTrackerCacheDto> {
  private readonly selectedPeriodIdState = signal<UUID | undefined>(undefined);

  private readonly emailsByTrackerId = signal<Record<UUID, ResponseEmailDto[]>>({});

  private readonly reminderTrackerPresenceState = signal<Map<UUID, ReminderTrackerPresenceEntry>>(new Map());

  private readonly reminderTrackerPresenceLoadedState = signal<boolean>(false);

  private readonly hasActiveNotifiableUserState = signal<boolean>(false);

  public readonly reminderTrackerPresence = this.reminderTrackerPresenceState.asReadonly();

  public readonly reminderTrackerPresenceLoaded = this.reminderTrackerPresenceLoadedState.asReadonly();

  public readonly hasActiveNotifiableUser = this.hasActiveNotifiableUserState.asReadonly();

  public readonly selectedPeriodId = this.selectedPeriodIdState.asReadonly();

  public readonly selectedPeriod = computed(() => {
    const periodId = this.selectedPeriodIdState();
    if (!periodId) {
      return;
    }
    return this.items().find((p) => p.id === periodId);
  });

  /**
   * Sets periods. Expects periods sorted by year DESC.
   * Persisting them is the facade's responsibility — the store stays free of side effects.
   *
   * @param {ResponseDeliveryTrackerCacheDto[]} periods - The list of delivery tracker periods to set
   * @returns {void} This method does not return a value.
   */
  public setPeriods(periods: ResponseDeliveryTrackerCacheDto[]): void {
    this.setItems(periods);
  }

  /**
   * Sets the selected period by its tracker ID.
   *
   * @param {UUID | undefined} periodId - The ID of the period to select, or undefined to clear the selection
   * @returns {void} This method does not return a value.
   */
  public setSelectedPeriodId(periodId: UUID | undefined): void {
    this.selectedPeriodIdState.set(periodId);
  }

  /**
   * Clears all in-memory state including periods, selected period, and email cache.
   * Clearing the persisted cache is the facade's responsibility.
   *
   * @returns {void} This method does not return a value.
   */
  public clearAll(): void {
    this.resetState();
    this.selectedPeriodIdState.set(undefined);
    this.emailsByTrackerId.set({});
    this.reminderTrackerPresenceState.set(new Map());
    this.reminderTrackerPresenceLoadedState.set(false);
    this.hasActiveNotifiableUserState.set(false);
  }

  /**
   * Replaces the reminder tracker presence map and the active-user flag, then marks presence as loaded.
   *
   * @param {Map<UUID, ReminderTrackerPresenceEntry>} presence - The presence map keyed by delivery tracker ID.
   * @param {boolean} hasActiveNotifiableUser - Whether the organization has any active RiskAnalysisCustomer user.
   * @returns {void} This method does not return a value.
   */
  public setReminderTrackerPresence(
    presence: Map<UUID, ReminderTrackerPresenceEntry>,
    hasActiveNotifiableUser: boolean,
  ): void {
    this.reminderTrackerPresenceState.set(presence);
    this.hasActiveNotifiableUserState.set(hasActiveNotifiableUser);
    this.reminderTrackerPresenceLoadedState.set(true);
  }

  /**
   * Retrieves cached emails for a specific tracker.
   *
   * @param {UUID} trackerId - The tracker ID to retrieve emails for
   * @returns {ResponseEmailDto[]} The cached emails, or an empty array if none are cached
   */
  public getEmailsForTracker(trackerId: UUID): ResponseEmailDto[] {
    return this.emailsByTrackerId()[trackerId] ?? [];
  }

  /**
   * Checks whether emails are cached for a specific tracker.
   *
   * @param {UUID} trackerId - The tracker ID to check
   * @returns {boolean} True if emails have been cached for the given tracker
   */
  public hasEmailsForTracker(trackerId: UUID): boolean {
    return trackerId in this.emailsByTrackerId();
  }

  /**
   * Removes cached emails for a specific tracker, forcing a fresh fetch on next request.
   *
   * @param {UUID} trackerId - The tracker ID to clear emails for
   * @returns {void} This method does not return a value.
   */
  public clearEmailsForTracker(trackerId: UUID): void {
    this.emailsByTrackerId.update((map) => {
      const { [trackerId]: _, ...rest } = map;
      return rest;
    });
  }

  /**
   * Stores emails for a specific tracker in the email cache.
   *
   * @param {UUID} trackerId - The tracker ID to associate the emails with
   * @param {ResponseEmailDto[]} emails - The list of emails to cache
   * @returns {void} This method does not return a value.
   */
  public setEmailsForTracker(trackerId: UUID, emails: ResponseEmailDto[]): void {
    this.emailsByTrackerId.update((map) => ({
      ...map,
      [trackerId]: emails,
    }));
  }
}
